import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import isEqual from 'lodash/isEqual'
import sortBy from 'lodash/sortBy'
import pluralize from 'pluralize'
import { isMobilePhone } from 'validator'

import { getParamValue } from 'selectors/pageStateSelectors'
import { STATE_KEY } from 'reducers/appStatusReducers'
import { getEntity, findAllEntities } from 'selectors/entitySelectors'

import { getIsLoggedIn } from 'selectors/authSelectors'
import { isKnownCustomer } from 'selectors/naughtySelectors'

import { WHEN_INCLUDE } from 'utils'

export const TIMEOUT_THRESHOLD = 5000000
export const HIDDEN_PREFIX = 'x'
export const REQUEST_SEP = '_'

export const USER_ROLES = {
  customer: 'Promoted to Full Customer (Gone)',
  ninja: 'Ninja (Sales/BDC Agent)',
  samurai: 'Samurai (Sales Manager)',
  sensei: 'Admin',
}

export const CONFIG_ATTRS = ['condition', 'year', 'make', 'model', 'trim']

export const getStatus = state => get(state, `[${STATE_KEY}].status`, ''),
  getMessage = state => get(state, `[${STATE_KEY}].message`, ''),
  getAppVersion = state => get(state, `[${STATE_KEY}].appVersion`, ''),
  getBrowser = state => {
    return {
      ...get(state, `[${STATE_KEY}].browser`, ''),
      referrer: window.document.referrer,
    }
  },
  getSessionId = state => get(state, `[${STATE_KEY}].sessionId`, null),
  getFingerPrint = state => get(state, `[${STATE_KEY}].fingerprint`, null),
  getBrowserName = state => get(state, `[${STATE_KEY}].browser.name`, ''),
  isBrowserIos = state =>
    get(state, `[${STATE_KEY}].browser.name`, '') === 'ios',
  isAndroid = state =>
    get(state, `[${STATE_KEY}].browser.os`, '') === 'Android OS',
  isMobile = state => isBrowserIos(state) || isAndroid(state),
  isBot = state => !!get(state, `[${STATE_KEY}].browser.bot`, false),
  isPaymentIQView = () => {
    try {
      return window.self !== window.top
    } catch (e) {
      return true
    }
  },
  getTheme = state =>
    get(state, `[${STATE_KEY}].demoInfo.theme`, '') ||
    get(state, `[${STATE_KEY}].rooftopInfo.theme`, ''),
  getRooftopName = state =>
    get(state, `[${STATE_KEY}].demoInfo.name`, '') ||
    get(state, `[${STATE_KEY}].rooftopInfo.name`, ''),
  getRooftopZipcode = state =>
    get(state, `[${STATE_KEY}].rooftopInfo.address.postalCode`, ''),
  getRooftopDefaultCustomerZip = state =>
    get(
      getRooftopConfigGroup(state, 'quote'),
      'scanOptions[0].customerZipCode',
      '',
    ),
  getRooftopId = state => getParamValue(state, 'dealerId'),
  getRooftopInfo = state => get(state, `[${STATE_KEY}].rooftopInfo`, {}),
  getRooftopUuid = state => get(getRooftopInfo(state), 'uuid'),
  getRooftopWebsite = state =>
    get(
      state,
      `[${STATE_KEY}].rooftopInfo.websiteDomain`,
      'www.frikintech.com',
    ),
  getTPDealerID = state =>
    get(state, `[${STATE_KEY}].rooftopInfo.config.tradepending`, {}),
  getFBPixelId = state =>
    get(state, `[${STATE_KEY}].rooftopInfo.config.facebookTrackingPixelId`),
  getGoogleTagManagerId = state =>
    get(state, `[${STATE_KEY}].rooftopInfo.config.googleTagManagerId`),
  getRooftopConfigGroup = (state, group, def) =>
    get(state, `[${STATE_KEY}].rooftopInfo.config.${group}`, def),
  isLoadingConfigGroup = (state, group, def) =>
    isEmpty(get(state, `[${STATE_KEY}].rooftopInfo.config.${group}`, def)),
  getActiveRequests = state => get(state, `[${STATE_KEY}].outstandingRequests`),
  getLastRetry = state => get(state, `[${STATE_KEY}].lastRetry`),
  getRequest = (state, requestKey) =>
    get(state, `[${STATE_KEY}].outstandingRequests[${requestKey}]`, ''),
  isActiveRequest = (state, requestKey) => {
    let reqTimestamp = getRequest(state, requestKey),
      now = new Date().getTime()

    return !!reqTimestamp && now - reqTimestamp < TIMEOUT_THRESHOLD
  },
  getRooftopUrl = (state, addendum) => {
    let rooftopId = getRooftopId(state)

    return [window.location.origin, rooftopId, addendum]
      .filter(x => x)
      .join('/')
  },
  anyActiveRequests = state => {
    let requests = get(state, `[${STATE_KEY}].outstandingRequests`, {})

    return (
      Object.keys(requests)
        .map(r => r.split(REQUEST_SEP)[0] !== HIDDEN_PREFIX)
        .filter(r => r).length > 0
    )
  },
  isRoofTopLoading = state => isEmpty(getRooftopInfo(state)),
  getBanks = state => get(state, `[${STATE_KEY}].banks`, []),
  getBankByCode = (state, code) =>
    get(
      getBanks(state).find(
        b =>
          Object.keys(b.codes).includes && Object.keys(b.codes).includes(code),
      ),
      'name',
      code,
    ),
  isPreparedQuote = (state, qId) => {
    let preparedQId = get(state, `[${STATE_KEY}].shortCodeQuoteId`)

    return !qId || preparedQId === qId
  },
  getHours = state => get(getRooftopInfo(state), 'hours'),
  getWhyBuy = state => get(getRooftopInfo(state), 'whybuy'),
  getAddress = state => get(getRooftopInfo(state), 'address', {}),
  getPhoneNumber = (state, type = 'main') =>
    get(
      get(getRooftopInfo(state), 'phone', []).find(p => p.type === type),
      'number',
    ),
  isLoadingInteractions = state =>
    get(state, `[${STATE_KEY}].loadingQuoteInteractions`, false),
  getErrorsFor = (state, errorsKey) =>
    get(state, `[${STATE_KEY}].errors.${errorsKey}`, {}),
  getUser = (state, id) => getEntity(state, id),
  getUsersList = state => {
    return findAllEntities(state, e => get(e, '_entityType') === 'user')
  },
  getFindingUser = state =>
    get(state, `[${STATE_KEY}].findingUser.finding`, false),
  getFoundUser = state => get(state, `[${STATE_KEY}].findingUser.user`, null),
  getAvailableConfigs = state => {
    return get(state, `${STATE_KEY}.availableConfigs`, {})
  },
  getDefaultQuoteConfig = () => {},
  getCurrentConfigIdentifiers = (state, configId) => {
    let config = get(getAvailableConfigs(state), configId, {})

    let ret = CONFIG_ATTRS.reduce(
      (ret, k) => ({ ...ret, [k]: config[k] || null }),
      {},
    )

    return ret
  },
  makeChooserLabel = config => {
    let { condition, year, make, model, trim } = config || {},
      label = `${[condition, year, make, model, trim].filter(x => x).join(' ')}`

    label = !make && !model ? `${label} Vehicles` : label
    label = !make || !model ? `All ${label}` : label

    return pluralize(label)
  },
  getConfigVehicleValues = (state, key) =>
    get(state, `[${STATE_KEY}].${key}`, []),
  getConfigChooserValues = (state, category) => {
    let availableConfigs = getConfigsForCategory(state, category)

    return sortBy(Object.keys(availableConfigs), k =>
      makeChooserLabel(availableConfigs[k]),
    ).reduce(
      (ret, k) => ({ ...ret, [k]: makeChooserLabel(availableConfigs[k], k) }),
      {},
    )
  },
  getMatchingExisitngConfig = (state, identifiers) => {
    let existingConfigs = getAvailableConfigs(state)

    return Object.keys(existingConfigs).some(k =>
      isEqual(existingConfigs[k], identifiers),
    )
  },
  getGlobalConfigIdFromAvailable = (state, category) => {
    let available = getConfigsForCategory(state, category)

    return Object.keys(available).find(k =>
      CONFIG_ATTRS.every(a => {
        let v = get(available, `${k}.${a}`)

        return isNull(v)
      }),
    )
  },
  getActiveConfig = state => ({
    ...get(state, `[${STATE_KEY}].activeConfig`, {}),
  }),
  getIsGettingConfig = state =>
    get(state, `[${STATE_KEY}].isGettingConfig`, {}),
  getConfigLabel = (state, id) => {
    return makeChooserLabel(get(state, `[${STATE_KEY}].availableConfigs.${id}`))
  },
  getConfig = (state, id) =>
    get(state, `[${STATE_KEY}].availableConfigs.${id}.config`, null),
  getConfigsForCategory = (state, category) => {
    let configs = getAvailableConfigs(state)

    return Object.keys(configs).reduce((ret, k) => {
      return configs[k].category === category
        ? { ...ret, [k]: configs[k] }
        : ret
    }, {})
  },
  getIsKnownCustomerFromState = state => {
    let customer = getTaggedCustomerFromState(state),
      user = getIsLoggedIn(state)

    return (
      !isEmpty(user) ||
      !isEmpty(get(customer, 'email')) ||
      !isEmpty(get(customer, 'phone')) ||
      get(customer, '_entityType') === 'dealer'
    )
  },
  getTaggedCustomerFromState = state =>
    get(state, `[${STATE_KEY}].customer`, {}),
  getCreditAppLlink = state =>
    get(getRooftopInfo(state), 'config.quote.displayOptions.creditAppLink'),
  getDrLink = state =>
    get(getRooftopInfo(state), 'config.quote.displayOptions.drLink'),
  getEnableDealerPolicyLink = state =>
    get(
      getRooftopInfo(state),
      'config.quote.displayOptions.enableDealerPolicyLink',
    ),
  getAccessoriesLink = state =>
    get(getRooftopInfo(state), 'config.quote.displayOptions.accessoriesLink'),
  getShowHomeDelivery = state =>
    get(getRooftopInfo(state), 'config.quote.displayOptions.homeDelivery'),
  getRooftopSalesPhone = state => {
    let phone = get(
      getRooftopInfo(state),
      'config.quote.displayOptions.salesPhone',
    )

    return isMobilePhone(phone || '') ? phone : null
  },
  getPaymentMods = state => {
    let { paymentRangePercent = 0, paymentRoundTo = 0 } = get(
      getRooftopInfo(state),
      'config.quote.scanOptions[0]',
      {},
    )

    return {
      paymentRangePercent: parseFloat(paymentRangePercent, 10),
      paymentRoundTo: parseInt(paymentRoundTo, 10),
    }
  },
  getShouldIncludeSetting = async (state, key) => {
    let isLoggedIn = !isEmpty(getIsLoggedIn(state)),
      isKnown = await isKnownCustomer(state),
      includeVal = get(
        getRooftopInfo(state),
        `config.quote.scanOptions[0].${key}`,
      )

    return (
      !isEmpty(isLoggedIn) ||
      includeVal === WHEN_INCLUDE.ALWAYS ||
      (includeVal === WHEN_INCLUDE.AUTO && isKnown)
    )
  },
  getShouldIncludeTaxesSetting = state => {
    return getShouldIncludeSetting(state, 'includeTaxes')
  },
  getShouldIncludeFeesSetting = state => {
    return getShouldIncludeSetting(state, 'includeFees')
  },
  getHidePriceToUnknownSetting = state => {
    return get(
      getRooftopInfo(state),
      'config.quote.scanOptions[0].hidePriceForUnknown',
    )
  },
  getDefaultSort = state => {
    return get(
      getRooftopInfo(state),
      'config.quote.displayOptions.defaultSort',
      'lease.payment.low',
    )
  },
  getShowAndTellList = state => {
    return get(state, `[${STATE_KEY}].rooftopInfo.showAndTell`, '')
  },
  getHasDraiver = state => {
    return get(state, `[${STATE_KEY}].rooftopInfo.hasDraiver`, false)
  },
  getSocketHealth = state => {
    return get(state, `[${STATE_KEY}].socketHealth`, '')
  },
  getSqueezeCount = state => {
    return get(state, `[${STATE_KEY}].squeezeCount`, '')
  },
  getAbeChatPosition = state => {
    return get(state, `[${STATE_KEY}].abeChatPosition`, '')
  },
  getScripts = state => {
    return get(state, `[${STATE_KEY}].rooftopInfo.scripts`, [])
  }
